import React from 'react';
import {
  CalendarOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { Button, DatePicker, Input, Select, Tooltip } from 'antd';
import type { TaskLink, TaskPriority } from '../../api/tasks/interface';
import type { CreateTaskInput } from '../../data/tasks/useTasksHook';
import { nextPriority } from './grouping';
import type { TaskLinkOption } from './TasksPanel';
import styles from './TasksTab.module.scss';

const PRIORITY_CLASS: Record<TaskPriority, string> = {
  high: styles.prioHigh,
  med: styles.prioMed,
  low: styles.prioLow,
};

const LINK_GROUP_LABEL: Record<TaskLink['type'], string> = {
  task: 'Tasks',
  artifact: 'Artifacts',
  note: 'Notes',
};

const LINK_GROUP_ORDER: TaskLink['type'][] = ['task', 'artifact', 'note'];

interface Props {
  onAdd: (input: CreateTaskInput) => void;
  onAddWithDetails: (input: CreateTaskInput) => void;
  assigneeOptions: string[];
  defaultAssignee: string;
  linkOptions: TaskLinkOption[];
}

const QuickAdd: React.FC<Props> = ({
  onAdd,
  onAddWithDetails,
  assigneeOptions,
  defaultAssignee,
  linkOptions,
}) => {
  const [title, setTitle] = React.useState('');
  const [priority, setPriority] = React.useState<TaskPriority>('med');
  const [due, setDue] = React.useState<Dayjs | null>(null);
  const [assignee, setAssignee] = React.useState<string>(defaultAssignee);
  const [linkId, setLinkId] = React.useState<string | undefined>(undefined);

  // The default arrives once the session/users load — adopt it until the user
  // explicitly picks someone.
  React.useEffect(() => {
    if (!assignee && defaultAssignee) {
      setAssignee(defaultAssignee);
    }
  }, [defaultAssignee, assignee]);

  const options = React.useMemo(() => {
    const set = new Set(assigneeOptions);
    if (assignee) {
      set.add(assignee);
    }
    return Array.from(set).map((a) => ({ label: a, value: a }));
  }, [assigneeOptions, assignee]);

  // Group link options by record type; the select value is `type:id` so a
  // single dropdown can mix tasks, artifacts and notes unambiguously.
  const linkGroups = React.useMemo(
    () =>
      LINK_GROUP_ORDER.map((type) => ({
        label: LINK_GROUP_LABEL[type],
        options: linkOptions
          .filter((o) => o.type === type)
          .map((o) => ({ label: o.label, value: `${o.type}:${o.value}` })),
      })).filter((g) => g.options.length > 0),
    [linkOptions]
  );

  const buildInput = (taskTitle: string): CreateTaskInput => {
    const linkOption = linkOptions.find(
      (o) => `${o.type}:${o.value}` === linkId
    );
    return {
      title: taskTitle,
      priority,
      due: due ? due.format('YYYY-MM-DD') : null,
      assignee: assignee || defaultAssignee || null,
      link: linkOption
        ? {
            type: linkOption.type,
            id: linkOption.value,
            label: linkOption.label,
          }
        : null,
      source: 'manual',
    };
  };

  const reset = () => {
    setTitle('');
    setDue(null);
    setPriority('med');
    setAssignee(defaultAssignee);
    setLinkId(undefined);
  };

  const add = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      return;
    }
    onAdd(buildInput(trimmed));
    reset();
  };

  // Create the task now, then open its detail so you can fill in description,
  // attachments, comments — the full editing surface (matches the design).
  const addWithDetails = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      return;
    }
    onAddWithDetails(buildInput(trimmed));
    reset();
  };

  return (
    <div className={styles.quickAdd}>
      <button
        type="button"
        className={`${styles.prio} ${styles.prioMini} ${PRIORITY_CLASS[priority]}`}
        title={`Priority: ${priority}`}
        onClick={() => setPriority(nextPriority(priority))}
      >
        <span className={styles.prioDot} />
      </button>
      <Input
        className={styles.quickAddInput}
        variant="borderless"
        placeholder="Add a task…"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onPressEnter={add}
      />
      <Select
        className={styles.quickAddAssignee}
        size="small"
        variant="borderless"
        showSearch
        value={assignee || undefined}
        placeholder="Assignee"
        options={options}
        onChange={setAssignee}
        suffixIcon={<UserOutlined />}
        popupMatchSelectWidth={false}
        optionFilterProp="label"
      />
      {linkGroups.length > 0 && (
        <Select
          className={styles.quickAddLink}
          size="small"
          variant="borderless"
          allowClear
          showSearch
          value={linkId}
          placeholder="Link record"
          options={linkGroups}
          onChange={setLinkId}
          suffixIcon={<LinkOutlined />}
          // Cap the dropdown width so long record names don't blow it out;
          // each option truncates with an ellipsis (full text on hover).
          popupMatchSelectWidth={320}
          optionFilterProp="label"
          optionRender={(option) => (
            <span
              className={styles.linkOptionLabel}
              title={String(option.label ?? '')}
            >
              {option.label}
            </span>
          )}
        />
      )}
      <DatePicker
        value={due}
        onChange={setDue}
        format="MMM D"
        placeholder="Due"
        variant="borderless"
        suffixIcon={<CalendarOutlined />}
      />
      <Tooltip title="Add with details">
        <Button
          type="text"
          shape="circle"
          icon={<EditOutlined />}
          disabled={!title.trim()}
          onClick={addWithDetails}
          aria-label="Add task with details"
        />
      </Tooltip>
      <Button
        type="primary"
        shape="circle"
        icon={<PlusOutlined />}
        disabled={!title.trim()}
        onClick={add}
        aria-label="Add task"
      />
    </div>
  );
};

export default QuickAdd;
